
/* eslint-disable @typescript-eslint/camelcase */
import { Options, prop, Vue } from "vue-class-component";
import RadioButton from "primevue/radiobutton";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { ModalMessageEntities, OptionsClass } from "@/domain/entities/MainApp";
import { UserController } from "@/app/ui/controllers/UserController";
import Input from "@/app/ui/components/input/index.vue";
import { RoleEntities } from "@/domain/entities/User";
import {
  EditRoleApiRequest,
  EditAssigned3lcApiRequest
} from "@/data/payload/api/UserApiRequest";
import { parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import { LocationController } from "@/app/ui/controllers/LocationController";
import { debounce } from "lodash";
import { CityData } from "@/domain/entities/Location";
import { HubController } from "@/app/ui/controllers/HubController";
import { RequestHubList, Hub } from "@/domain/entities/Hub";

class Props {
  modelValue = prop<boolean>({
    type: Boolean,
    default: false
  });
  type = prop<string>({
    type: String,
    default: ""
  });
}

@Options({
  emits: ["update:modelValue", "success"],
  components: {
    RadioButton,
    Input
  }
})
class ModalEditDetail extends Vue.with(Props) {
  assigned3lc = [] as OptionsClass[];
  focusHub = false;
  hubPage = 1;
  hubName: OptionsClass = new OptionsClass();
  loadingHub = false;
  listHub: Hub[] = [];
  loadingDivision = false;
  divisionName: any = "";
  loadingShuttle = false;
  shuttleName: any = "";

  mounted() {
    this.fetchListHub();
    this.assigned3lc =
      this.dataDetail.assigned3lc
        ?.split(",")
        .map((lc) => new OptionsClass({ name: lc.trim(), value: lc.trim() })) ||
      [];
    if (this.dataDetail.hub.hubId)
      this.hubName = new OptionsClass({
        name: this.dataDetail.hub.hubName,
        value: this.dataDetail.hub.hubId.toString()
      });
    else this.hubName = new OptionsClass();
    if (this.dataDetail.shuttle_id) {
      this.shuttleName = new OptionsClass({
        name: this.dataDetail.shuttle_name,
        value: this.dataDetail.shuttle_id.toString()
      });
    } else {
      this.shuttleName = new OptionsClass({
        name: String(this.$t("userManagement.modalEditDetail.noShuttle")),
        value: "0"
      });
    }
  }
  get title() {
    switch (this.type) {
      case "status":
        return this.$t("userManagement.modalEditDetail.titleStatus");
      case "email":
        return this.$t("userManagement.modalEditDetail.titleEmail");
      case "role":
        return this.$t("userManagement.modalEditDetail.titleRole");
      case "3lc":
        return this.$t("userManagement.modalEditDetail.title3lc");
      default:
        return this.$t("userManagement.modalEditDetail.titleDefault");
    }
  }
  toggleModalEditDetail(bool: boolean) {
    this.$emit("update:modelValue", bool);
  }
  openSnackbar(message: string) {
    MainAppController.setSnackbarMessage(message);
    MainAppController.setSnackbar(true);
  }
  get isFormValid() {
    switch (this.type) {
      case "status":
        return this.statusAccount !== this.currentStatusAccount;
      case "role":
        return !!this.roleDefault.value;
      case "email":
        return !this.errorFormEmail;
      case "3lc":
        return this.assigned3lc.length;
      case "hub":
        return (
          this.hubName.value === null ||
          !!this.hubName.value ||
          this.isReferenceEmptyHub
        );
      case "division":
        return !!this.divisionName;
      case "shuttle":
        return this.shuttleName.value === null || !!this.shuttleName;
      default:
        return false;
    }
  }
  submitConfirmation() {
    switch (this.type) {
      case "status":
        this.confirmationEditStatus();
        break;
      case "email":
        this.confirmationEdit();
        break;
      case "role":
        this.confirmationEdit();
        break;
      case "3lc":
        this.confirmationEditAssigned3lc();
        break;
      case "hub":
        this.confirmationEditHub();
        break;
      case "division":
        this.confirmationEditDivision();
        break;
      case "shuttle":
        this.confirmationEditShuttle();
        break;
      default:
        break;
    }
  }

  get currentStatusAccount() {
    return UserController.userDetail.status.toLowerCase();
  }

  // confirmation change status
  statusAccount = UserController.userDetail.status.toLowerCase();
  confirmationEditStatus() {
    this.toggleModalEditDetail(false);
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        title: this.$t("userManagement.modalEditDetail.confirmStatusTitle", {
          action:
            this.statusAccount === "active"
              ? this.$t("userManagement.modalEditDetail.activation")
              : this.$t("userManagement.modalEditDetail.deactivation")
        }),
        message: this.$t(
          "userManagement.modalEditDetail.statusInactiveMessage"
        ),
        textCancel: this.$t("userManagement.modalEditDetail.back"),
        textSuccess:
          this.statusAccount === "active"
            ? this.$t("userManagement.modalEditDetail.activate")
            : this.$t("userManagement.modalEditDetail.deactivate"),
        onSubmit: () => this.submitEditStatus(),
        onClose: () => {
          this.toggleModalEditDetail(true);
          MainAppController.closeMessageModal();
        },
        image: "are-you-sure"
      })
    );
  }
  async submitEditStatus() {
    MainAppController.closeMessageModal();
    const resp = await UserController._onEditUser(
      this.editPayload(
        String(
          this.$t("userManagement.modalEditDetail.payloadTypeStatusAccount")
        )
      )
    );
    if (resp) {
      this.openSnackbar(
        String(
          this.$t("userManagement.modalEditDetail.statusSuccess", {
            action:
              this.statusAccount === "active"
                ? this.$t("userManagement.modalEditDetail.activation")
                : this.$t("userManagement.modalEditDetail.deactivation")
          })
        )
      );
      this.$emit("success");
    }
  }

  // confirmation change email
  // new email
  newEmail = "";
  get errorFormEmail() {
    return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.newEmail);
  }
  // old email
  get oldEmail() {
    return UserController.userDetail.email;
  }

  editPayload(
    type = String(this.$t("userManagement.modalEditDetail.payloadTypeEmail"))
  ) {
    const assigned3lc = this.assigned3lc.map((lc) => lc.value).join(",");
    return {
      id: Number(UserController.userDetail.id),
      isActive: this.statusAccount,
      email: this.newEmail,
      type,
      assigned3lc: assigned3lc,
      hubId: Number(this.hubName.value) || 0,
      divisionId: this.divisionName.value,
      shuttleId: this.type === "hub" ? 0 : Number(this.shuttleName.value) || 0
    };
  }

  confirmationEdit() {
    this.toggleModalEditDetail(false);
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        title: this.$t("userManagement.modalEditDetail.confirmChangeTitle"),
        message: this.$t(
          "userManagement.modalEditDetail.confirmChangeMessage",
          {
            username: UserController.userDetail.username
          }
        ),
        textCancel: this.$t("userManagement.modalEditDetail.back"),
        textSuccess: this.$t("userManagement.modalEditDetail.save"),
        onSubmit:
          this.type === "email" ? this.submitEditEmail : this.submitEditRole,
        onClose: () => {
          this.toggleModalEditDetail(true);
          MainAppController.closeMessageModal();
        },
        image: "are-you-sure"
      })
    );
  }
  confirmationEditHub() {
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        title: this.$t("userManagement.modalEditDetail.confirmHubTitle"),
        message: this.$t("userManagement.modalEditDetail.confirmHubMessage"),
        textCancel: this.$t("userManagement.modalEditDetail.back"),
        textSuccess: this.$t("userManagement.modalEditDetail.yesSave"),
        image: "badge-confirmation-general",
        onSubmit: this.submitEditHub,
        customWidth: "417px",
        imageWidth: "32",
        onClose: () => {
          MainAppController.closeMessageModal();
        }
      })
    );
  }

  confirmationEditDivision() {
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        title: this.$t("userManagement.modalEditDetail.confirmDivisionTitle"),
        message: this.$t(
          "userManagement.modalEditDetail.confirmDivisionMessage"
        ),
        textCancel: this.$t("userManagement.modalEditDetail.back"),
        textSuccess: this.$t("userManagement.modalEditDetail.yesSave"),
        image: "badge-confirmation-general",
        onSubmit: this.submitEditHub,
        customWidth: "417px",
        imageWidth: "32",
        onClose: () => {
          MainAppController.closeMessageModal();
        }
      })
    );
  }

  confirmationEditShuttle() {
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        title: this.$t("userManagement.modalEditDetail.confirmShuttleTitle"),
        message: this.$t(
          "userManagement.modalEditDetail.confirmShuttleMessage"
        ),
        textCancel: this.$t("userManagement.modalEditDetail.back"),
        textSuccess: this.$t("userManagement.modalEditDetail.yesSave"),
        image: "badge-confirmation-general",
        onSubmit: this.submitEditHub,
        customWidth: "417px",
        imageWidth: "32",
        onClose: () => {
          MainAppController.closeMessageModal();
        }
      })
    );
  }

  async submitEditHub() {
    MainAppController.closeMessageModal();
    const resp = await UserController._onEditUser(this.editPayload());
    if (resp) {
      MainAppController.showMessageModal(
        new ModalMessageEntities({
          title: this.$t("userManagement.modalEditDetail.hubUpdatedTitle"),
          textSuccess: this.$t("userManagement.modalEditDetail.ok"),
          image: "badge-success",
          customWidth: "417px",
          imageWidth: "32",
          onSubmit: () => {
            MainAppController.closeMessageModal();
            this.toggleModalEditDetail(false);
            this.$router.push(`/user-management`);
          }
        })
      );
    }
  }

  async submitEditEmail() {
    MainAppController.closeMessageModal();
    const resp = await UserController._onEditUser(this.editPayload());
    if (resp) {
      this.openSnackbar(
        String(this.$t("userManagement.modalEditDetail.emailUpdatedSuccess"))
      );
      this.$emit("success");
    }
  }

  // Role
  get roleDefault(): OptionsClass {
    return new OptionsClass(UserController.roleDefault);
  }
  setRoleDefault(data: OptionsClass) {
    UserController.setRoleDefault(data);
  }
  get roleData() {
    return UserController.roleData.map(
      (item: RoleEntities) =>
        new OptionsClass({
          name: item.name,
          value: String(item.id)
        })
    );
  }
  async submitEditRole() {
    const assigned3lc = this.assigned3lc.map((lc) => lc.value).join(",");
    try {
      MainAppController.showLoading();
      MainAppController.closeMessageModal();
      MainAppController.closeErrorMessage();
      await UserController.editRole(
        new EditRoleApiRequest({
          roleId: Number(this.roleDefault.value),
          accountId: UserController.userDetail.id,
          status: this.statusAccount,
          assigned_3lc: assigned3lc
        })
      );
      this.openSnackbar(
        String(this.$t("userManagement.modalEditDetail.roleUpdatedSuccess"))
      );
      this.$emit("success");
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          String(this.$t("userManagement.modalEditDetail.roleUpdateFailed")),
          this.submitEditRole
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  get dataDetail() {
    return UserController.userDetail;
  }

  dataListCity: CityData[] = [];

  // origin city
  async fetchCity(search: string) {
    const response = await LocationController.getCityList({
      search: search,
      status: "active",
      page: 1,
      limit: 20,
      newEndpoint: true
    });

    this.dataListCity = response.cityData;
  }
  filterCity = debounce((search: string) => {
    if (search.length > 2 || !search) this.fetchCity(search);
  }, 250);

  async fetchCityinfiniteScroll(search = "") {
    LocationController.cityData.pagination.page++;
    const response = await LocationController.getCityList({
      search: search,
      status: "active",
      page: LocationController.cityData.pagination.page,
      limit: 20,
      newEndpoint: true
    });

    this.dataListCity.push(...response.cityData);
  }

  get cityExcludeOptions() {
    return this.dataListCity.map((e) => ({
      id: e.id,
      name: `${e.code}`,
      value: `${e.code}`
    }));
  }

  get cityOptions() {
    return [
      {
        name: String(this.$t("userManagement.modalEditDetail.all")),
        value: "ALL"
      },
      ...this.cityExcludeOptions
    ];
  }
  get isLoadingCity() {
    return LocationController.isLoading;
  }

  confirmationEditAssigned3lc() {
    this.toggleModalEditDetail(false);
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        title: this.$t(
          "userManagement.modalEditDetail.assigned3lcConfirmTitle"
        ),
        message: this.$t(
          "userManagement.modalEditDetail.assigned3lcConfirmMessage"
        ),
        textCancel: this.$t("userManagement.modalEditDetail.back"),
        textSuccess: this.$t("userManagement.modalEditDetail.save"),
        onSubmit: () => {
          this.submitEditAssigned3lc();
        },
        onClose: () => {
          this.toggleModalEditDetail(true);
          MainAppController.closeMessageModal();
        },
        image: "are-you-sure"
      })
    );
  }

  async submitEditAssigned3lc() {
    const accountId = UserController.userDetail.id;
    const assigned3lc = this.assigned3lc.map((lc) => lc.value).join(",");
    MainAppController.closeMessageModal();
    const resp = await UserController.editAssigned3lc(
      new EditAssigned3lcApiRequest({
        accountId: accountId,
        status: this.statusAccount,
        assigned_3lc: assigned3lc
      })
    );
    if (resp) {
      MainAppController.showMessageModal(
        new ModalMessageEntities({
          title: this.$t(
            "userManagement.modalEditDetail.assigned3lcUpdatedTitle"
          ),
          message: this.$t(
            "userManagement.modalEditDetail.assigned3lcUpdatedMessage",
            {
              accountId,
              assigned3lc
            }
          ),
          image: "image-modal-success",
          textSuccess: this.$t("userManagement.modalEditDetail.ok"),
          onSubmit: () => {
            MainAppController.closeMessageModal();
            this.$emit("success");
          }
        })
      );
    }
  }

  get listHubDataList() {
    return [
      ...this.listHub.map(
        (hub) =>
          new OptionsClass({
            name: hub.hubName,
            value: hub.hubId.toString()
          })
      )
    ];
  }

  isReferenceEmptyHub = false;
  async fetchListHub() {
    if (this.type !== "hub") return;
    try {
      this.isReferenceEmptyHub = false;
      this.focusHub = true;
      this.listHub = [];
      this.hubPage = 1;
      this.loadingHub = true;
      const hubList = await HubController.getHubList(
        new RequestHubList({
          page: this.hubPage,
          partnerId: this.dataDetail.partner.id,
          city: this.dataDetail.partner.partner_location.city_code,
          status: "active",
          limit: 10
        })
      );

      this.listHub = hubList.data;
      await this.$nextTick();
      this.handleFirstFetchHubList();
    } catch (err) {
      this.listHub = [];
    } finally {
      this.loadingHub = false;
    }
  }

  handleFirstFetchHubList() {
    if (this.listHub.length === 1) {
      this.hubName = this.listHubDataList[0];
    }
    if (!this.listHub.length) {
      this.isReferenceEmptyHub = true;
    }
  }

  async fetchListHubInfiniteScroll() {
    this.hubPage++;
    try {
      this.loadingHub = true;
      const hubList = await HubController.getHubList(
        new RequestHubList({
          page: this.hubPage,
          partnerId: this.dataDetail.partner.id,
          city: this.dataDetail.partner.partner_location.city_code,
          status: "active",
          limit: 10
        })
      );

      this.listHub.push(...hubList.data);
    } catch (err) {
      this.listHub = [];
    } finally {
      this.loadingHub = false;
    }
  }

  onSelectHub(value: OptionsClass) {
    if (!value) return (this.hubName = new OptionsClass());
    this.hubName = value;
  }

  // Field Division
  focusListDivision() {
    this.loadingDivision = true;
    this.fetchListDivision("");
  }
  fetchListDivision = debounce(async (search: string) => {
    this.loadingDivision = true;
    await UserController.getDivision(search);
    this.loadingDivision = false;
  }, 250);

  get divisionDataList() {
    return UserController.divisionDataList.map((item) => ({
      name: item.divisionName,
      value: item.divisionId
    }));
  }
  onSelectDivision(value: any) {
    if (!value) return (this.divisionName = "");
    this.divisionName = value;
  }
  // Field Shuttle
  focusListShuttle() {
    this.loadingShuttle = true;
    this.fetchListShuttle("");
  }

  fetchListShuttle = debounce(async (search: string) => {
    this.loadingShuttle = true;

    const hubId = this.dataDetail.hub.hubId;
    const partnerId =
      this.dataDetail.client !== undefined
        ? this.dataDetail.client.id
        : this.dataDetail.partner !== undefined
        ? this.dataDetail.partner.id
        : this.dataDetail.vendor !== undefined
        ? this.dataDetail.vendor.id
        : 0;

    await UserController.getShuttle({
      search,
      hubId,
      partnerId
    });

    this.loadingShuttle = false;
  }, 250);

  get shuttleDataList() {
    return [
      {
        name: String(this.$t("userManagement.modalEditDetail.noShuttle")),
        value: "0"
      },
      ...UserController.shuttleDataList.map((item) => ({
        name: item.shuttleName,
        value: item.shuttleId
      }))
    ];
  }
  onSelectShuttle(value: any) {
    if (!value) return (this.shuttleName = "");
    this.shuttleName = value;
  }
}
export default ModalEditDetail;
